import { useState, useRef } from "react";
import { FileText, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "components/ui/use-toast";
import { useImportTransactionsMutation } from "@/services/cav-offline-sync";
import { ImportResponse } from "@/services/cav-offline-sync/types";
import { validateImportCSV } from "../utils/validate-import-csv";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { generateTemplateCSV, downloadFile } from "../utils/csv-export";

interface ImportInstructionsContentProps {
  onSuccess: (result: ImportResponse) => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const WARNING_ROW_COUNT = 10000;

export function ImportInstructionsContent({
  onSuccess,
}: ImportInstructionsContentProps) {
  const { handleModal } = useDialogModal();
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importTransactions, { isLoading }] = useImportTransactionsMutation();

  const handleClose = () => {
    if (!isLoading) {
      handleModal({ isOpen: false, element: null, title: "" });
    }
  };

  const downloadTemplate = () => {
    const content = generateTemplateCSV();
    downloadFile(content, "template-importacao-cav.csv");
  };

  const processFile = async (file: File) => {
    setFileError(null);

    // Validate file extension
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setFileError("Apenas arquivos .csv sao aceitos");
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setFileError("Arquivo excede o limite de 5MB");
      return;
    }

    // Validate CSV structure
    const validation = await validateImportCSV(file);
    if (!validation.valid) {
      setFileError(validation.error || "Arquivo invalido");
      return;
    }

    if (validation.rowCount && validation.rowCount > WARNING_ROW_COUNT) {
      toast({
        description: `Arquivo com ${validation.rowCount.toLocaleString("pt-BR")} linhas. Isso pode demorar.`,
      });
    }

    // Upload file
    try {
      const result = await importTransactions(file).unwrap();
      onSuccess(result);
      handleModal({ isOpen: false, element: null, title: "" });
    } catch (err: unknown) {
      const error = err as { status?: number; data?: { message?: string } };
      if (error.status === 503) {
        toast({
          description: "Servico indisponivel. Tente novamente mais tarde.",
          variant: "destructive",
        });
      } else {
        toast({
          description: error.data?.message || "Erro ao importar arquivo.",
          variant: "destructive",
        });
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDropZoneClick = () => {
    if (!isLoading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // Reset file input
    e.target.value = "";
  };

  return (
    <div className="space-y-4">
      {/* Description */}
      <p className="text-sm text-gray-600">
        Utilize esta funcao para marcar transacoes como processadas manualmente.
        Envie um arquivo CSV contendo as transacoes que foram resolvidas fora do sistema.
      </p>

      {/* Instructions Section */}
      <div className="space-y-3">
        <p className="text-sm font-medium">
          Formato esperado do arquivo CSV:
        </p>

        <div className="space-y-2">
          <p className="text-sm font-medium">Colunas obrigatorias:</p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4">
            <li>
              <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">
                CAVNUM_LOJA
              </span>{" "}
              - Numero da loja (ex: 123)
            </li>
            <li>
              <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">
                CAVDOCCOD
              </span>{" "}
              - Codigo do documento (ex: 1000001)
            </li>
          </ul>
        </div>

        {/* CSV Example */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Exemplo:</p>
          <div className="bg-gray-50 border rounded-md p-3">
            <pre className="text-xs font-mono text-gray-700">
              CAVNUM_LOJA,CAVDOCCOD{"\n"}
              123,1000001{"\n"}
              123,1000002{"\n"}
              456,2000001
            </pre>
          </div>
        </div>

        {/* Download Template Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={downloadTemplate}
          className="w-full sm:w-auto"
        >
          <Download className="h-4 w-4 mr-2" />
          Baixar Template
        </Button>
      </div>

      {/* Separator */}
      <div className="border-t" />

      {/* Drag and Drop Zone */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleDropZoneClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleDropZoneClick();
          }
        }}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-8
          flex flex-col items-center justify-center gap-2
          cursor-pointer transition-colors
          ${isDragging
            ? "border-primary bg-primary/5"
            : "border-gray-300 hover:border-gray-400"
          }
          ${isLoading ? "pointer-events-none opacity-50" : ""}
        `}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
            <p className="text-sm text-gray-500">Enviando arquivo...</p>
          </>
        ) : (
          <>
            <FileText className="h-8 w-8 text-gray-400" />
            <p className="text-sm text-gray-600 text-center">
              Arraste o arquivo CSV aqui
              <br />
              ou clique para selecionar
            </p>
          </>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        className="hidden"
        disabled={isLoading}
      />

      {/* Error message */}
      {fileError && (
        <p className="text-sm text-red-500 text-center">{fileError}</p>
      )}

      {/* File constraints */}
      <p className="text-xs text-gray-500 text-center">
        Arquivo maximo: 5MB | Maximo de linhas: 50.000
      </p>

      <div className="flex justify-end">
        <Button variant="outline" onClick={handleClose} disabled={isLoading}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
